import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment/moment"
import nProgress from "nprogress"
import axios from "axios"

import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import Table from "components/table"
import { Plus, Pencil } from "components/icons/outline"
import { lang } from "config"

import useProductTypeGroupRepositories from "repositories/product-type-group"
import GroupingModal from "./modal" // create this file
import Delete from "pages/product/type-group/delete"
import Filter from "pages/product/type-group/filter"

export default function List() {
  const { can } = useAuth()
  const { title } = useOutletContext()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchInput = useRef()
  const [searchTemp, setSearchTemp] = useState(searchParams.get("search") || "")

  useEffect(() => {
    setSearchTemp(searchParams.get("search") || "")
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("search")])

  const submitSearch = () => {
    const next = new URLSearchParams(searchParams)
    if (searchTemp) next.set("search", searchTemp)
    else next.delete("search")
    next.delete("page")
    setSearchParams(next)
  }

  const page = searchParams.get("page")
  const search = searchParams.get("search")
  const name = searchParams.get("name")
  const from = searchParams.get("from")
  const to = searchParams.get("to")
  const orderBy = searchParams.get("order_by")
  const direction = searchParams.get("direction")

  const {
    data: groupData,
    isLoading: isLoadingGroups,
    mutate: mutateGroups,
  } = useProductTypeGroupRepositories({
    page, search, name, from, to, order_by: orderBy, direction
  })

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [search])

  useEffect(() => {
    if (isLoadingGroups) nProgress.start()
    else nProgress.done()
  }, [isLoadingGroups])

  const updateFilter = (value) => setSearchParams(value)
  const removeFilter = () => setSearchParams({})

  const toggleSort = (value) => {
    if ((direction === null || direction === "desc") || orderBy !== value) {
      setSearchParams({ order_by: value, direction: "asc" })
    } else {
      setSearchParams({ order_by: value, direction: "desc" })
    }
  }

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null) // null => create mode

  const openCreate = () => {
    setEditingGroup(null)
    setModalOpen(true)
  }

  const openEdit = (groupRow) => {
    setEditingGroup(groupRow)
    setModalOpen(true)
  }

  const closeModal = () => setModalOpen(false)
  const assetBaseUrl = process.env.REACT_APP_INVENTORY_API_URL || ""

    const resolveImage = (path) => {
    if (!path) return ""
    const p = String(path)
    if (p.startsWith("http")) return p
    const clean = p.replace(/^\/+/, "")
    return assetBaseUrl ? `${assetBaseUrl}/${clean}` : `/${clean}`
    }

    const initials = (name = "") =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("")

  const dataListHeaders = [
    { isAction: false, content: "Nama Group", sortKey: "name" },
    { isAction: false, content: "Items", sortKey: null },
    { isAction: false, content: "Dibuat Pada", sortKey: "created_at" },
    { isAction: false, content: "Diubah Pada", sortKey: "updated_at" },
    { isAction: true, content: null },
  ]

  const dataListColumns = [
    {
    type: "default",
    bold: false,
    content: (v) => {
        const src = resolveImage(v.image_url)

        return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl border overflow-hidden bg-neutral-50 flex items-center justify-center">
            {src ? (
                <img
                src={src}
                alt={v.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                    // fallback to blank (will show initials)
                    e.currentTarget.src = ""
                }}
                />
            ) : (
                <div className="text-[10px] font-semibold text-neutral-500">
                {initials(v.name) || "—"}
                </div>
            )}
            </div>

            <div className="min-w-0">
            <div className="font-semibold text-xs truncate">{v.name}</div>
            <div className="text-[10px] text-neutral-400 truncate">{v.id}</div>
            </div>
        </div>
        )
    },
    },
    {
      type: "default",
      bold: false,
      content: (v) => {
        const items = v.product_types || v.productTypes || []
        return (
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 text-[10px] border rounded-full">
              {items.length} item(s)
            </span>
            {items.map((pt) => (
              <span key={pt.id} className="px-2 py-1 text-[10px] bg-neutral-100 rounded-full">
                {pt.name}
              </span>
            ))}
          </div>
        )
      },
    },
    { type: "default", bold: false, content: (v) => moment(v.created_at).format("MMMM D, YYYY") },
    { type: "default", bold: false, content: (v) => moment(v.updated_at).format("MMMM D, YYYY") },
    {
      type: "action",
      content: (v) => (
        <>
          {can("product-type-group.update") && (
            <button
              type="button"
              onClick={() => openEdit(v)}
              className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
              title="Edit"
            >
              <Pencil className="w-6 h-6" />
            </button>
          )}

          {can("product-type-group.delete") && (
            <Delete data={v} onSuccess={() => mutateGroups()} />
          )}
        </>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center sm:space-x-2">
          <div className="relative hidden sm:block">
            <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <circle cx={10} cy={10} r={7}></circle>
                <line x1={21} y1={21} x2={15} y2={15}></line>
              </svg>
            </div>
            <input
              ref={searchInput}
              onChange={(e) => setSearchTemp(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
              value={searchTemp}
              type="text"
              placeholder={`${lang.search} Group`}
              autoComplete="off"
              className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>

          {/* If you already have a filter component, keep it.
              Otherwise remove these 2 lines. */}
          {/* <Filter onSubmit={updateFilter} onRemove={removeFilter} data={{ name, from, to }} /> */}
          <Filter
            onSubmit={updateFilter}
            onRemove={removeFilter}
            data={Object.fromEntries(Object.entries({ name, from, to }).filter(([_, v]) => v != null))}
            />
        </div>

        <div className="flex items-center space-x-2">
          {can("product-type-group.create") && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
            >
              <Plus className="w-4 h-4" stroke={1.5} />
              <span>Buat Group</span>
            </button>
          )}
        </div>
      </div>

      <Table
        isLoading={isLoadingGroups}
        data={groupData?.data ?? []}
        headers={dataListHeaders}
        columns={dataListColumns}
        key={(v) => v.id}
        isEmpty={groupData?.data?.length === 0 && !search && !isLoadingGroups}
        isEmptyOnSearching={groupData?.data?.length === 0 && search && !isLoadingGroups}
        toggleSort={toggleSort}
        orderBy={orderBy}
        direction={direction}
      />

      <Pagination
        links={groupData?.links}
        from={groupData?.from}
        to={groupData?.to}
        total={groupData?.total}
      />

      <GroupingModal
        open={modalOpen}
        onClose={closeModal}
        group={editingGroup} // null => create mode
        onSaved={() => mutateGroups()}
      />
    </div>
  )
}
